// Botón discreto arriba del grid de teléfonos: «Promos activas (N) ▼». Al click despliega un panel
// con las promos vigentes de la tienda (venta + crédito). Las promos del equipo elegido se ven en
// el panel derecho (ProductDetailCard). Diseñado para evitar los dos banners cuadrados que llenaban
// el header del paso 04 y repetían info del panel derecho.
import { useEffect, useRef, useState, type RefObject } from 'react';
import { Tag, ChevronDown, X } from 'lucide-react';
import { useStoreSalePromotions } from '@/domains/retail/promotions-wizard/api/useWizardPromotions';
import type { CreditPromotionRow } from '../creditPromotions';

export function PromoShortcutButton({
    sessionId,
    scopeWideCreditRows,
}: {
    sessionId: string;
    /** Filas de crédito de alcance tienda, calculadas por el step a partir del catálogo + sesión. */
    scopeWideCreditRows: readonly CreditPromotionRow[];
}) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    useOutsideClick(containerRef, () => {
        setOpen(false);
    });
    const { data: storePromos } = useStoreSalePromotions(sessionId);
    const salePromos = storePromos?.items ?? [];

    const total = salePromos.length + scopeWideCreditRows.length;
    if (total === 0) return null;

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                onClick={() => {
                    setOpen((v) => !v);
                }}
                aria-expanded={open}
                className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-[12px] font-medium shadow-sm hover:bg-muted"
            >
                <Tag className="size-3.5 text-muted-foreground" aria-hidden />
                <span>
                    Promos activas <span className="tabular-nums">({total})</span>
                </span>
                <ChevronDown
                    className={
                        'size-3.5 text-muted-foreground transition-transform ' +
                        (open ? 'rotate-180' : '')
                    }
                    aria-hidden
                />
            </button>
            {open && (
                <div
                    role="dialog"
                    aria-label="Promos activas en la tienda"
                    className="absolute right-0 top-full z-20 mt-2 w-[min(28rem,90vw)] rounded-md border border-border bg-card p-3 shadow-lg"
                >
                    <div className="mb-2 flex items-center justify-between">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Promos vigentes en la tienda
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                            }}
                            className="rounded p-1 text-muted-foreground hover:bg-muted"
                            aria-label="Cerrar"
                        >
                            <X className="size-3.5" aria-hidden />
                        </button>
                    </div>
                    {salePromos.length > 0 && (
                        <section className="mb-3">
                            <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
                                De venta ({salePromos.length})
                            </h3>
                            <ul className="flex flex-col gap-1">
                                {salePromos.map((p) => (
                                    <li
                                        key={p.promotionId}
                                        className="rounded bg-emerald-50/60 px-2 py-1 text-[12px]"
                                    >
                                        <div className="font-semibold text-emerald-900">
                                            {p.name}
                                        </div>
                                        {p.customerText && (
                                            <div className="text-[11px] text-emerald-900/70">
                                                {p.customerText}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                    {scopeWideCreditRows.length > 0 && (
                        <section>
                            <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-violet-800">
                                De crédito ({scopeWideCreditRows.length})
                            </h3>
                            <ul className="flex flex-col gap-1">
                                {scopeWideCreditRows.map((r) => (
                                    <li
                                        key={r.promotionId}
                                        className="rounded bg-violet-50/60 px-2 py-1 text-[12px]"
                                    >
                                        <div className="font-semibold text-violet-900">
                                            {r.name}
                                        </div>
                                        {r.description && (
                                            <div className="text-[11px] text-violet-900/70">
                                                {r.description}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                    <p className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
                        Las promos que aplican al equipo elegido se ven en el panel derecho.
                    </p>
                </div>
            )}
        </div>
    );
}

function useOutsideClick(ref: RefObject<HTMLElement | null>, onOutside: () => void) {
    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            if (!ref.current || ref.current.contains(e.target as Node)) return;
            onOutside();
        };
        window.addEventListener('mousedown', onDown);
        return () => {
            window.removeEventListener('mousedown', onDown);
        };
    }, [ref, onOutside]);
}
